import { DiscountTemplate } from "@/types/billing/discount";
import { ItemType } from "@/types/enums/item-type";
import { ItemScanResolution } from "@/types/inventory/barcode";
import {
  Item,
  ItemDetail,
  ItemFilters,
  ItemGroupItem,
} from "@/types/inventory/item";
import { Shipment } from "@/types/inventory/shipment";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export interface CheckoutItemLotFilters {
  stockId?: string;
  locationId?: string;
  lotNumber?: string;
  includeDepleted?: boolean;
  shipmentIds?: string[];
}

/** Billing-scoped receipt discovery/hydration; never grants general inventory access. */
export async function getCheckoutItemLots(
  itemId: string,
  filters: CheckoutItemLotFilters,
): Promise<Shipment[] | null> {
  return requestData<Shipment[]>(`inventory/items/${itemId}/lots`, "get", {
    searchParams: {
      stock_id: filters.stockId,
      location_id: filters.locationId,
      lot_number: filters.lotNumber,
      include_depleted: filters.includeDepleted,
      shipment_ids: filters.shipmentIds,
    },
  });
}

/**
 * Flatten ItemFilters for use as searchParams.
 * Extracts the nested `usage` object into top-level `usageValue`/`usageComparison`
 * and strips the `usage` key so cleanSearchParams can handle it.
 */
function flattenItemFilters(
  filters?: ItemFilters,
): Record<string, unknown> | undefined {
  if (!filters) return undefined;
  const { usage, id, ids, includeArchived, categoryExact, ...rest } = filters;
  const filterIds = Array.from(new Set([...(ids ?? []), ...(id ? [id] : [])]));

  return {
    ...rest,
    ...(includeArchived !== undefined && { include_archived: includeArchived }),
    ...(categoryExact !== undefined && { category_exact: categoryExact }),
    ...(filterIds.length > 0 && { ids: filterIds }),
    ...(usage?.value !== undefined && { usageValue: usage.value }),
    ...(usage?.comparison && { usageComparison: usage.comparison }),
  };
}

export interface ItemSearchResult {
  id: string;
  name: string;
  price: number;
  type: ItemType;
  taxable: boolean;
  /** Catalog category the item sits in, when it has one. */
  category?: string | null;
  /** For packages: the package's contents (non-archived discount templates). */
  discountTemplates?: DiscountTemplate[];
}

export async function searchItems(
  name: string,
  itemTypes?: ItemType[],
  limit?: number,
  locationId?: string,
  signal?: AbortSignal,
): Promise<ItemSearchResult[] | null> {
  return await requestData<ItemSearchResult[]>(
    "inventory/items/search",
    "get",
    {
      searchParams: {
        name,
        ...(itemTypes && { item_type: itemTypes }),
        ...(limit && { limit }),
        ...(locationId && { location_id: locationId }),
      },
      ...(signal && { signal }),
    },
  );
}

/**
 * Resolve a raw barcode string into a sellable item (or a picker for
 * ambiguous matches). Same auth surface and `itemTypes` filter as
 * {@link searchItems} — the scanner surfaces items the same user is allowed
 * to type in, optionally narrowed further by item type.
 */
export async function scanItem(
  rawValue: string,
  itemTypes?: ItemType[],
): Promise<ItemScanResolution | null> {
  return await requestData<ItemScanResolution>(
    "inventory/items/scan",
    "get",
    {
      searchParams: {
        rawValue,
        ...(itemTypes && { item_type: itemTypes }),
      },
    },
    false,
  );
}

export async function listItemGroups(
  filters?: ItemFilters,
): Promise<ItemGroupItem[] | null> {
  return await requestData<ItemGroupItem[]>("inventory/items/groups", "get", {
    searchParams: flattenItemFilters(filters),
  });
}

export async function listItems(
  filters?: ItemFilters,
  signal?: AbortSignal,
): Promise<Page<ItemDetail> | null> {
  return await requestData<Page<ItemDetail>>("inventory/items/list", "get", {
    searchParams: flattenItemFilters(filters),
    ...(signal && { signal }),
  });
}

export async function deleteItem(itemId: string): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/items/delete/${itemId}`,
    "delete",
  );
}

export async function saveNewItem(item: Item): Promise<Item | null> {
  return await requestData<Item>("inventory/items/create", "post", {
    json: item,
  });
}

export async function saveNewGiftCardItem(
  item: Item,
): Promise<ItemDetail | null> {
  return await requestData<ItemDetail>(
    "inventory/items/create-gift-card",
    "post",
    { json: item },
  );
}

export async function updateExistingItem(
  itemId: string,
  itemUpdate: Partial<Item>,
): Promise<Item | null> {
  return await requestData<Item>(`inventory/items/update/${itemId}`, "put", {
    json: itemUpdate,
  });
}

export interface CatalogItemImageChange {
  image?: string | null;
  imageUrl?: string | null;
  imageFile?: File | null;
  shouldRemoveImage?: boolean;
}

export interface CatalogItemImageResponse {
  itemId: string;
  image: string | null;
  imageUrl: string | null;
}

export async function uploadItemImage(
  itemId: string,
  imageFile: File,
): Promise<CatalogItemImageResponse | null> {
  const formData = new FormData();
  formData.append("image_file", imageFile);

  return await requestData<CatalogItemImageResponse>(
    `inventory/items/${itemId}/image`,
    "put",
    { body: formData },
    false,
  );
}

export async function removeItemImage(
  itemId: string,
): Promise<CatalogItemImageResponse | null> {
  const result = await requestData<Item>(
    `inventory/items/update/${itemId}`,
    "put",
    { json: { image: null } },
    false,
  );
  if (!result) return null;
  // Clearing the cover promotes the next gallery photo, if there is one.
  if (result.image) {
    const gallery = await getItemGallery(itemId);
    const cover = gallery?.images[0];
    return {
      itemId,
      image: cover?.image ?? result.image,
      imageUrl: cover?.imageUrl ?? null,
    };
  }
  return { itemId: result.id ?? itemId, image: null, imageUrl: null };
}

export async function saveCatalogItemImageChange(
  itemId: string,
  value: CatalogItemImageChange,
): Promise<CatalogItemImageResponse | null> {
  if (value.imageFile) {
    return await uploadItemImage(itemId, value.imageFile);
  }

  if (value.shouldRemoveImage) {
    return await removeItemImage(itemId);
  }

  return null;
}

export interface ItemGalleryImage {
  image: string;
  imageUrl: string | null;
}

/** First image is the cover (`item.image`). */
export interface ItemGalleryResponse {
  itemId: string;
  images: ItemGalleryImage[];
}

export async function getItemGallery(
  itemId: string,
): Promise<ItemGalleryResponse | null> {
  return await requestData<ItemGalleryResponse>(
    `inventory/items/${itemId}/gallery`,
    "get",
    undefined,
    false,
  );
}

export async function uploadItemGalleryImages(
  itemId: string,
  imageFiles: File[],
): Promise<ItemGalleryResponse | null> {
  const formData = new FormData();
  for (const file of imageFiles) {
    formData.append("image_files", file);
  }

  return await requestData<ItemGalleryResponse>(
    `inventory/items/${itemId}/gallery`,
    "post",
    { body: formData },
    false,
  );
}

/** `images` is the stored paths in the new order; omitted photos are removed. */
export async function updateItemGallery(
  itemId: string,
  images: string[],
): Promise<ItemGalleryResponse | null> {
  return await requestData<ItemGalleryResponse>(
    `inventory/items/${itemId}/gallery`,
    "put",
    { json: { images } },
    false,
  );
}

export async function fetchItemBrandNames(): Promise<string[] | null> {
  return await requestData<string[]>("inventory/items/brand-names", "get");
}

export interface ItemTaxUpdate {
  itemId: string;
  exemptedTaxIds?: string[] | null;
}

export async function bulkUpdateItemTaxes(
  updates: ItemTaxUpdate[],
): Promise<Item[] | null> {
  return await requestData<Item[]>(
    "inventory/items/bulk-update-taxes",
    "post",
    {
      json: { updates },
    },
  );
}

export interface ItemSortOrderEntry {
  itemId: string;
  sortOrder: number;
}

export async function bulkUpdateItemSortOrder(
  items: ItemSortOrderEntry[],
): Promise<Item[] | null> {
  return await requestData<Item[]>(
    "inventory/items/bulk-update-sort-order",
    "post",
    {
      json: { items },
    },
  );
}

export async function getItem(itemId: string): Promise<ItemDetail | null> {
  return await requestData<ItemDetail>(`inventory/items/${itemId}`, "get");
}

export async function convertProductToMedication(
  itemId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/items/${itemId}/convert-to-medication`,
    "post",
    undefined,
    false,
  );
}

export async function convertMedicationToProduct(
  itemId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/items/${itemId}/convert-to-product`,
    "post",
    undefined,
    false,
  );
}

export const itemApi = {
  getAll: listItems,
  get: getItem,
  create: saveNewItem,
  update: updateExistingItem,
  delete: deleteItem,
  getBrandNames: fetchItemBrandNames,
  convertToMedication: convertProductToMedication,
  convertToProduct: convertMedicationToProduct,
  uploadImage: uploadItemImage,
  removeImage: removeItemImage,
  saveImageChange: saveCatalogItemImageChange,
};
